import {
    DownloadIcon,
    FileIcon,
    LoaderIcon,
    PencilIcon,
    TrashIcon,
    TriangleAlertIcon,
    XIcon,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { Drawer } from 'vaul';
import { FileSize } from '../../../components/typography/file-size.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useBreakpoint } from '../../../lib/hooks/use-breakpoints.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmAttachmentInput } from '../../../server/dto/crm-attachment-dto.ts';
import { api } from '../../../utils/api.ts';
import { ConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';

type FileCardProps = {
    file: CrmAttachmentInput;
    onRemove: (file: CrmAttachmentInput) => void;
    onEdit?: (file: CrmAttachmentInput) => void;
    onDownload?: (file: CrmAttachmentInput) => void;
};

export const FileCard = ({ file, onRemove, onEdit, onDownload }: FileCardProps) => {
    const { t } = useLocale('tasks');
    const { toast } = useToast();
    const { isSm } = useBreakpoint('sm');
    const [isDownloading, setIsDownloading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { data: urls, isFetching } = api.platoUser.query.uploadcare.getAttachmentUrls.useQuery({
        uuid: file.externalUuid,
        cdnUrl: file.cdnUrl,
        previewCdnUrl: file.previewCdnUrl,
        isImage: file.isImage,
        name: file.name,
    });

    const handleDownload = (): void => {
        if (!urls?.downloadUrl) {
            return;
        }

        setIsDownloading(true);

        try {
            const a = document.createElement('a');
            a.href = urls.downloadUrl;
            a.download = file.name;
            a.rel = 'noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            onDownload?.(file);
        } catch (err) {
            console.error('Error downloading file:', err);
            toast({ icon: 'warning', title: t('tasks__form__download_error') });
        } finally {
            setIsDownloading(false);
        }
    };

    const previewUrl = urls?.thumbnailUrl ?? null;

    const handleDelete = useCallback(() => {
        void DialogStateManager.show(ConfirmationDialog, {
            focus: 'destroy',
            icon: TriangleAlertIcon,
            title: t('tasks__form__delete_attachment_confirmation__title'),
            description: t('tasks__form__delete_attachment_confirmation__body'),
            accept: {
                text: t('form__delete', { ns: 'common' }),
                action: () => {
                    onRemove(file);
                },
            },
            reject: {
                text: t('form__cancel', { ns: 'common' }),
            },
        });
    }, [t, onRemove, file]);

    const handleCardClick = () => {
        if (!isSm) {
            setDrawerOpen(true);
        }
    };

    return (
        <>
            <div
                className="group/file-card relative flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
                onClick={handleCardClick}
                role="presentation"
            >
                {/* Desktop: hover buttons */}
                <div className="absolute top-2 right-2 z-10 hidden gap-2 sm:flex">
                    <Button
                        variant="ghost"
                        size="icon"
                        rounded
                        icon={DownloadIcon}
                        onClick={handleDownload}
                        isLoading={isDownloading}
                        disabled={isDownloading}
                        aria-label={t('tasks__form__download_file')}
                        className="bg-white/80 shadow-sm"
                    />
                    {file.isImage && onEdit && (
                        <Button
                            variant="ghost"
                            size="icon"
                            rounded
                            icon={PencilIcon}
                            onClick={() => {
                                onEdit(file);
                            }}
                            aria-label={t('tasks__form__edit_file')}
                            className="bg-white/80 shadow-sm"
                        />
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        rounded
                        icon={XIcon}
                        onClick={handleDelete}
                        className="bg-white/80 shadow-sm"
                    />
                </div>
                <div className="flex h-24 sm:h-32 items-center justify-center overflow-hidden bg-white p-3">
                    {previewUrl ? (
                        <img src={previewUrl} alt={file.name} className="h-full w-full object-contain" />
                    ) : isFetching ? (
                        <LoaderIcon className="h-8 w-8 animate-spin text-neutral-300" />
                    ) : (
                        <FileIcon className="h-10 w-10 text-neutral-400" />
                    )}
                </div>
                <div className="flex flex-col gap-0.5 px-3 py-2">
                    <Text size="sm" className="truncate">
                        {file.name}
                    </Text>
                    <Text size="xs" color="neutral-500">
                        <FileSize>{file.size}</FileSize>
                    </Text>
                </div>
            </div>

            <Drawer.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
                <Drawer.Portal>
                    <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
                    <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-white gap-2">
                        <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-neutral-300" />
                        <div className="flex px-5 pt-4 pb-2 gap-1 flex-col">
                            <Text size="base" weight="medium" className="truncate">
                                {file.name}
                            </Text>
                            <Text size="sm" color="neutral-500">
                                <FileSize>{file.size}</FileSize>
                            </Text>
                        </div>
                        <hr className="border-neutral-100" />
                        <div className="flex flex-col gap-1 px-3 pb-3">
                            <Button
                                variant="ghost"
                                leftIcon={DownloadIcon}
                                className="justify-start"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    handleDownload();
                                }}
                            >
                                {t('tasks__form__download_file')}
                            </Button>
                            {file.isImage && onEdit && (
                                <Button
                                    variant="ghost"
                                    leftIcon={PencilIcon}
                                    className="justify-start"
                                    onClick={() => {
                                        setDrawerOpen(false);
                                        onEdit(file);
                                    }}
                                >
                                    {t('tasks__form__edit_file')}
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                leftIcon={TrashIcon}
                                className="justify-start text-red-600"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    handleDelete();
                                }}
                            >
                                {t('form__delete', { ns: 'common' })}
                            </Button>
                        </div>
                        <div className="px-3 pb-5">
                            <Button
                                variant="subtle"
                                className="w-full"
                                onClick={() => {
                                    setDrawerOpen(false);
                                }}
                            >
                                {t('form__cancel', { ns: 'common' })}
                            </Button>
                        </div>
                    </Drawer.Content>
                </Drawer.Portal>
            </Drawer.Root>
        </>
    );
};
